import { Link } from "react-router-dom";
import { PUBLIC_ROUTES } from "../../routes";
import MarketingShell from "./MarketingShell";

const LAYERS = [
  {
    eyebrow: "The map",
    title: "She knows the room, not just the words.",
    body: "Arthur knows that the bathroom door sticks and that the rug in the hallway is the one Margaret trips on. RoboTwins knows where the conveyor ends and which bin the operator leans over. We build that map from a five-minute walkthrough — no installers, no rewiring. Without it, a robot is dangerous; with it, it&rsquo;s useful.",
    detail: "Under the hood: a world twin generated from video, with collision meshes and semantic tags. We call this Scan-to-Sim.",
    detailLink: PUBLIC_ROUTES.productScanToSim,
  },
  {
    eyebrow: "The mind",
    title: "She thinks about more than the last sentence.",
    body: "Arthur remembers that Tuesdays are quiet and the daughter calls on Sundays. RoboTwins remembers that the Friday changeover is the one that goes wrong. The cloud planner reasons over the room, the routine, and the day so far — and decides what to do next, or, more often, what not to do.",
    detail: "Under the hood: a cloud planner that reasons over the map, the history, and redacted abstractions from the Local Mind — never raw video — with every plan signed before it can act.",
    detailLink: PUBLIC_ROUTES.productIntelligenceCore,
  },
  {
    eyebrow: "The reflex",
    title: "Safety doesn&rsquo;t wait for the internet.",
    body: "If the WiFi drops, Arthur still catches a fall. If an operator steps into the cell, the arm is already stopping. The safety layer is small, fast, and lives on the robot itself — a sub-millisecond reflex loop that holds force limits, stops on contact, and refuses to do anything risky even if every other system goes dark.",
    detail: "Under the hood today: an on-robot control loop enforcing a configured safety envelope, subordinate to the robot vendor's own certified safety functions. A native Rust microkernel on the control bus is the next step, not the current state.",
    detailLink: PUBLIC_ROUTES.productEdgeReflex,
  },
  {
    eyebrow: "The record",
    title: "Every important move is written down — and signed.",
    body: "When Arthur calls the family, when a policy is refused at the envelope, that action is recorded in a tamper-proof ledger. Clinicians can ask why. Families can ask why. Regulators can verify any episode end-to-end. The robot never gets to hide what it did, and the people relying on it never have to guess.",
    detail: "Under the hood: a cryptographic intent ledger built for EU AI Act Article 12 record-keeping and Article 26(6) deployer log retention.",
    detailLink: PUBLIC_ROUTES.compliance,
  },
  {
    eyebrow: "The boundary",
    title: "What happens in the house stays in the house.",
    body: "A care robot sees bathrooms, bedrooms, and bad days — so the raw stream dies on the robot. Perception runs locally, on hardware you own; the cloud planner only ever receives signed abstractions — “a fall in the kitchen”, never footage. And every byte that does leave is itemised in the Egress Ledger, so a family doesn't have to trust a privacy policy. They can check.",
    detail: "Under the hood: on-device perception, redacted state abstraction, and a per-byte signed egress ledger. We call this the Local Mind.",
    detailLink: PUBLIC_ROUTES.productLocalMind,
  },
  {
    eyebrow: "The reason it can ship",
    title: "Insurance is the wall every humanoid hits. We&rsquo;re the layer that walks through it.",
    body: "Most humanoid robots can&rsquo;t be deployed in real care settings because nobody will underwrite them. Insurers want three things they almost never get from a humanoid: deterministic safety, an auditable record of every decision, and conformity to recognised standards. A robot running Arthur or RoboTwins becomes insurable because the five layers above provide exactly those three things — the Edge Reflex for the determinism, the Intent Ledger for the audit, and ISO 25785-1 alignment for the standards. We don&rsquo;t sell the robot. We sell the layer and the safety case that get someone else&rsquo;s robot through the front door.",
    detail: "Under the hood: ISO 25785-1 balance-operational alignment, sub-0.5 ms edge reflex, Ed25519-signed ledger — assembled into an underwriter-ready safety case.",
    detailLink: PUBLIC_ROUTES.compliance,
  },
] as const;

export default function HowItWorks() {
  return (
    <MarketingShell pageClass="mk-how-page">
      <section className="mk-how-hero">
        <p className="sg-eyebrow">How it works</p>
        <h1>We don&rsquo;t make the robot. We make the six things that run on it.</h1>
        <p className="mk-care-lede">
          Manifold Kinetics doesn&rsquo;t manufacture humanoids — we
          provide the layer that sits on top of whatever certified
          humanoid you choose. Arthur and RoboTwins are the same six
          ideas tuned for different rooms, including the ones most humanoid
          teams forget about until it&rsquo;s too late: how to keep
          the home&rsquo;s video in the home, and how to actually get
          the robot insured to be where you want it.
        </p>
      </section>

      <section className="mk-how-layers">
        {LAYERS.map((layer) => (
          <article className="mk-how-layer" key={layer.title}>
            <p className="sg-section-kicker">{layer.eyebrow}</p>
            <h2 dangerouslySetInnerHTML={{ __html: layer.title }} />
            <p
              className="mk-how-body"
              dangerouslySetInnerHTML={{ __html: layer.body }}
            />
            <p className="mk-how-detail">
              {layer.detail}{" "}
              <Link to={layer.detailLink}>Read the technical detail →</Link>
            </p>
          </article>
        ))}
      </section>

      <section className="sg-final-cta">
        <p className="sg-section-kicker">Built on RoboTwins</p>
        <h2>The same world-model engineering that runs industrial robots, applied to the people who need it most.</h2>
        <p>
          Manifold Kinetics is built on the RoboTwins platform —
          originally engineered to keep factory robots safe around
          humans. We&rsquo;ve taken that same care and turned it
          toward family homes, guided by one principle: a machine
          more capable than you should want you to thrive.
        </p>
        <div className="sg-hero-actions center">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Request a pilot
          </Link>
          <Link to={PUBLIC_ROUTES.about} className="sg-btn sg-btn-secondary">
            Why we built this
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
